import { Box, Center, Flex, FormControl, FormLabel, Text } from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";

import { $email, $ok, $UUID } from "foundation-ts/commons";
import { UUID } from "foundation-ts/types";
import { CorporationDto } from "g1-commons/lib/doxecureClientTypes";
import { corporationIdentifiersDictionary } from "g1-commons/lib/doxecureLocales";

import { ColumnTitle } from "@components/ColumnTitle/ColumnTitle";
import { ErrorField } from "@components/ErrorField" ;
import { G1Button } from "@components/G1Button";
import { G1Input } from "@components/G1Input";
import { SeaShellBox } from "@components/SeaShellBox" ;
import { UserInfosProperties, UserParameters } from "@components/UserInfos/UserInfos";
import { useDevice } from "@hooks/useDevice";
import { getCorporation } from "@services/corporations.service";
import { $g1Color } from "@/theme/G1Style";
import { userRoleAndStatus } from "@utils/functions";
import { G1Icon } from "@utils/icons";

type CorporationUserInfosValues = {
    firstName: string,
    lastName: string,
    email: string,
    apid: string,
    corporationIdentifier: string
} ;

export const CorporationUserInfos = (props: UserInfosProperties) => {
    const { data, actionButtons, maxWidth, visibility, isAdminCorporate, isCreation, onBack } = props ;
    const { isMOBILE } = useDevice() ;
    const [isDisabledUserInfos, setIsDisabledUserInfos] = useState($ok(data)) ;
    const [corporation, setCorporation] = useState<CorporationDto|null>(null) ;
    const {
        handleSubmit,
        register,
        reset,
        setValue,
        formState: { errors },
    } = useForm<any>({
        mode: "onChange",
        reValidateMode: "onChange",
    });

    const mailPattern = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i;

    const handleChange = (e) => {
        const { name, value } = e.target;
        setValue(name, value) ;
    } ;

    const onSubmit: SubmitHandler<any> = (data: CorporationUserInfosValues, event) => {
        const dataUser: UserParameters = {
            firstName: data.firstName,
            lastName: data.lastName,
            email: $email(data.email)!,
            corporationId: $UUID(corporation?.apid)!,
        } ;
        if ($ok(data.apid)) {
            dataUser.apid = $UUID(data.apid)! ;
        }

        event?.preventDefault() ;
        const button = (event?.nativeEvent as SubmitEvent).submitter as HTMLButtonElement;
        const buttonIndex = button.getAttribute('data-index');
        if ($ok(buttonIndex)) {
            actionButtons[buttonIndex!].onSubmit(dataUser) ;
        }
    } ;

    const renderCorporationInfos = () => {
        if (isAdminCorporate) {
            return <></> ;
        } else {
            return (
                <>
                    <FormControl isDisabled={isDisabledUserInfos || isCreation}>
                        <FormLabel fontSize="1.25em">Organisation</FormLabel>
                        <G1Input component="form" textTransform="uppercase" value={corporation?.name} />
                    </FormControl>
                    <FormControl isDisabled={isDisabledUserInfos || isCreation} marginTop={3}>
                        <FormLabel fontSize="1.25em">{`Identifiant "${$ok(corporation?.corporationIdentifierType) ? corporationIdentifiersDictionary[corporation!.corporationIdentifierType]?.name : ''}"`}</FormLabel>
                        <G1Input component="form" textTransform="uppercase" value={corporation?.corporationIdentifier} />
                    </FormControl>
                </>
            ) ;
        }
    }

    useEffect(() => {
        const getCorporationInfos = async (corporationId: UUID) => {
            const corporation = await getCorporation(corporationId) ;
            setCorporation(corporation) ;
        }

        reset() ;
        setIsDisabledUserInfos($ok(data?.apid)) ;
        setValue("lastName", data?.lastName || "") ;
        setValue("firstName", data?.firstName || "") ;
        setValue("email", $ok(data?.email) ? data?.email.toString() : "") ;
        setValue("apid", data?.apid || null) ;

        if ($ok(data?.corporationId)) {
            getCorporationInfos(data?.corporationId!) ;
        }
    }, [data]) ;

    return (
      <>
        <ColumnTitle display={isMOBILE ? "block" : "none"} color={$g1Color('global.write')}>
            <Flex alignItems="center" as="button" onClick={onBack} width="100%">
                <Center color={$g1Color('global.icons.navigation.previous.write')}><G1Icon.Back /></Center>
                <Text>Retour</Text>
            </Flex>
        </ColumnTitle>
        {!isAdminCorporate && isCreation && 
            <ColumnTitle color={$g1Color('global.write')}>
                <Text>Création d'un gestionnaire</Text>
            </ColumnTitle>
        }
        <Box visibility={visibility} width="100%" mt={10} maxW={maxWidth}>
            <SeaShellBox component="form">
                <form noValidate onSubmit={handleSubmit(onSubmit)}>
                    {renderCorporationInfos()}

                    <FormControl isRequired isDisabled={isDisabledUserInfos} marginTop={3}>
                        <FormLabel fontSize="1.25em">Nom</FormLabel>
                        <G1Input component="form" registerInput={{ ...register("lastName", { required: true }) }} textTransform="uppercase" onChange={handleChange} />
                        {errors.lastName && <ErrorField fieldName="nom" />}
                    </FormControl>

                    <FormControl isRequired isDisabled={isDisabledUserInfos} marginTop={3}>
                        <FormLabel fontSize="1.25em">Prénom</FormLabel>
                        <G1Input component="form" registerInput={{ ...register("firstName", { required: true }) }} textTransform="capitalize" onChange={handleChange} />
                        {errors.firstName && <ErrorField fieldName="prénom" />}
                    </FormControl>

                    <FormControl isRequired isDisabled={isDisabledUserInfos} marginTop={3}>
                        <FormLabel fontSize="1.25em">Adresse email</FormLabel>
                        <G1Input component="form" type="email" registerInput={{ ...register("email", { required: true, pattern: mailPattern }) }} textTransform="lowercase" autoComplete="true" onChange={handleChange} />
                        {errors.email && <ErrorField fieldName="email" error="Le champ email est requis ou est invalide" />}
                    </FormControl>

                    { isCreation ? <></> :
                        <>
                            <Text marginTop="20px" fontSize="md" fontWeight="bold" display="inline-block">
                                Profil:
                            </Text>
                            <Text marginTop="20px" marginLeft="5px" fontSize="md" display="inline-block">
                                { userRoleAndStatus(data?.userFlags!, isAdminCorporate) }
                            </Text>
                        </>
                    }

                    <FormControl>
                        <G1Input component="form" name="apid" type="hidden" />
                    </FormControl>

                    <Flex direction="row" justifyContent="center" marginTop="10px">
                        { actionButtons.length === 0 ? '' :
                                actionButtons.map((button, index) => (
                                    <G1Button component="form" variant={button.variant} type="submit" title={button.title} marginRight="2" fontSize="md" data-index={index} />
                                ))
                        }
                    </Flex>
                </form>
            </SeaShellBox>
        </Box>
      </>
    ) ;
} ;